import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { StatusGate } from './components/StatusGate';
import { AppToaster } from './components/common/AppToaster';
import { useAuthStore } from './store/authStore';
import { useThemeStore } from './store/themeStore';

// Páginas CRÍTICAS para boot: Inbox, Login — mantém síncrono
import { Inbox } from './pages/Inbox';
import { Login } from './pages/Login';

// Lazy: páginas pesadas apenas carregam quando acessadas
const Kanban = lazy(() => import('./pages/Kanban'));
const Relatorios = lazy(() => import('./pages/Relatorios'));
const Configuracoes = lazy(() => import('./pages/Configuracoes'));
const SuperAdminPanel = lazy(() => import('./pages/SuperAdminPanel'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const FollowDisparador = lazy(() => import('./pages/FollowDisparador'));
const FollowLogs = lazy(() => import('./pages/FollowLogs'));
const LeadList = lazy(() => import('./pages/LeadList'));
const Agenda = lazy(() => import('./pages/Agenda'));
const Catalog = lazy(() => import('./pages/Catalog'));
const AutomacoesInteligentes = lazy(() => import('./pages/AutomacoesInteligentes'));
const AutomacoesIaSdrConfig = lazy(() => import('./pages/AutomacoesIaSdrConfig'));
const Perfil = lazy(() => import('./pages/Perfil'));
// [TUTORIAIS] Importação sob demanda da Central de Tutoriais
const TutorialCenter = lazy(() => import('./pages/TutorialCenter'));
const NotasCampo = lazy(() => import('./pages/NotasCampo'));
const PublicSignup = lazy(() => import('./pages/PublicSignup'));
const Politica = lazy(() => import('./pages/Politica'));
const Termos = lazy(() => import('./pages/Termos'));

import { GlobalLoader } from './components/common/GlobalLoader';

// Fallback unificado para carregamento de módulos sob demanda
const FollowPageSkeleton = () => (<div className="flex min-h-screen flex-col md:flex-row"><div aria-hidden="true" className="hidden shrink-0 border-r border-slate-200 bg-white md:block md:w-[220px] dark:border-slate-800 dark:bg-slate-900" /><div className="min-w-0 flex-1"><PageSkeleton /></div></div>);
const PageSkeleton = () => (
  <div className="p-8 flex justify-center items-center">
    <GlobalLoader variant="container" size="sm" message="Carregando tela..." className="max-w-md w-full" />
  </div>
);

function RoleRoute({ roles, children }: { roles: string[], children: React.ReactNode }) {
  const profile = useAuthStore(state => state.profile);
  // Aceita a flag de proprietário da plataforma somente quando a rota já permitir super_admin; usuário comum permanece bloqueado
  if (!profile || !(roles.includes(profile.role) || (roles.includes('super_admin') && profile.is_platform_owner === true))) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function BetaRoute({ children }: { children: React.ReactNode }) {
  const profile = useAuthStore(state => state.profile);
  const betaEmails = ['edson.kaue.alves598gmn@gmail.com', 'cozmos.atendimento@gmail.com'];
  const isAllowed = profile && (
    profile.role === 'super_admin' || 
    profile.role === 'tenant_admin' || 
    (profile.email && betaEmails.includes(profile.email.toLowerCase()))
  );
  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function App() {
  const isDarkMode = useThemeStore(state => state.isDarkMode);
  const initializeAuth = useAuthStore(state => state.initialize);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  return (
    <BrowserRouter>
      <AppToaster />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Suspense fallback={<PageSkeleton />}><PublicSignup /></Suspense>} />
        <Route path="/politica" element={<Suspense fallback={<PageSkeleton />}><Politica /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<PageSkeleton />}><Politica /></Suspense>} />
        <Route path="/termos" element={<Suspense fallback={<PageSkeleton />}><Termos /></Suspense>} />
        <Route path="/terms" element={<Suspense fallback={<PageSkeleton />}><Termos /></Suspense>} />
        
        <Route path="/" element={<StatusGate><Layout /></StatusGate>}>
          <Route index element={<Suspense fallback={<PageSkeleton />}><Dashboard /></Suspense>} />
          <Route path="dashboard" element={<Suspense fallback={<PageSkeleton />}><Dashboard /></Suspense>} />
          <Route path="inbox" element={<Inbox />} />
          <Route path="kanban" element={<Suspense fallback={<PageSkeleton />}><Kanban /></Suspense>} />
          <Route path="leads" element={<Suspense fallback={<PageSkeleton />}><LeadList /></Suspense>} />
          <Route path="notas-campo" element={
            <BetaRoute>
              <Suspense fallback={<PageSkeleton />}><NotasCampo /></Suspense>
            </BetaRoute>
          } />
          <Route path="agenda" element={<Suspense fallback={<PageSkeleton />}><Agenda /></Suspense>} />
          <Route path="relatorios" element={<Suspense fallback={<PageSkeleton />}><Relatorios /></Suspense>} />
          <Route path="catalog" element={<Suspense fallback={<PageSkeleton />}><Catalog /></Suspense>} />
          <Route path="automacoes-ia" element={<Suspense fallback={<PageSkeleton />}><AutomacoesInteligentes /></Suspense>} />
          <Route path="automacoes-ia/sdr" element={<Suspense fallback={<PageSkeleton />}><AutomacoesIaSdrConfig /></Suspense>} />
          <Route path="follow-disparador" element={<Suspense fallback={<FollowPageSkeleton />}><FollowDisparador /></Suspense>} />
          <Route path="follow-logs" element={<Suspense fallback={<FollowPageSkeleton />}><FollowLogs /></Suspense>} />
          <Route path="perfil" element={<Suspense fallback={<PageSkeleton />}><Perfil /></Suspense>} />
          <Route path="site-origens" element={<Navigate to="/config" replace />} />
          <Route path="config" element={
            <RoleRoute roles={['super_admin', 'tenant_admin', 'gestor', 'owner_dev', 'admin', 'vendedor']}>
              <Suspense fallback={<PageSkeleton />}><Configuracoes /></Suspense>
            </RoleRoute>
          } />
          <Route path="admin" element={
            <RoleRoute roles={['super_admin']}>
              <Suspense fallback={<PageSkeleton />}><SuperAdminPanel /></Suspense>
            </RoleRoute>
          } />
          {/* [TUTORIAIS] Rotas da Central de Tutoriais */}
          <Route path="tutoriais" element={<Suspense fallback={<PageSkeleton />}><TutorialCenter /></Suspense>} />
          <Route path="tutoriais/modulos/:moduleId" element={<Suspense fallback={<PageSkeleton />}><TutorialCenter /></Suspense>} />
          <Route path="tutoriais/aulas/:tutorialId" element={<Suspense fallback={<PageSkeleton />}><TutorialCenter /></Suspense>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
